import type { CropUnit, HistoryResultMetadata } from '../../state/historyStore';

export interface CountryOption {
  code: string;
  labelKey: string;
  defaultUnit: CropUnit;
}

export interface CropPresetVariation {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  widthMm: number;
  heightMm: number;
  countries?: string[];
}

export interface CropPresetOption {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  countries?: string[];
  variations?: CropPresetVariation[];
}

export interface CropPresetGroup {
  id: string;
  titleKey: string;
  descriptionKey?: string;
  icon?: string;
  options: CropPresetOption[];
}

export interface FlattenedPreset {
  id: string;
  labelKey: string;
  descriptionKey?: string;
  widthMm: number;
  heightMm: number;
  countries?: string[];
  parentLabelKey?: string;
}

export const COUNTRY_OPTIONS: CountryOption[] = [
  { code: 'CN', labelKey: 'history.crop.countries.cn', defaultUnit: 'mm' },
  { code: 'US', labelKey: 'history.crop.countries.us', defaultUnit: 'in' },
  { code: 'JP', labelKey: 'history.crop.countries.jp', defaultUnit: 'mm' }
];

export const CROP_PRESET_GROUPS: CropPresetGroup[] = [
  {
    id: 'id-docs',
    titleKey: 'history.crop.groups.idDocs.title',
    descriptionKey: '',
    icon: '🎫',
    options: [
      {
        id: 'one-inch',
        labelKey: 'history.crop.presets.oneInch.title',
        descriptionKey: 'history.crop.presets.oneInch.description',
        countries: ['CN'],
        variations: [
          {
            id: 'one-inch-standard',
            labelKey: 'history.crop.presets.oneInch.standard',
            descriptionKey: 'history.crop.presets.oneInch.standardHint',
            widthMm: 25,
            heightMm: 35,
            countries: ['CN']
          },
          {
            id: 'one-inch-small',
            labelKey: 'history.crop.presets.oneInch.small',
            descriptionKey: 'history.crop.presets.oneInch.smallHint',
            widthMm: 22,
            heightMm: 32,
            countries: ['CN']
          },
          {
            id: 'one-inch-large',
            labelKey: 'history.crop.presets.oneInch.large',
            descriptionKey: 'history.crop.presets.oneInch.largeHint',
            widthMm: 33,
            heightMm: 48,
            countries: ['CN']
          }
        ]
      },
      {
        id: 'two-inch',
        labelKey: 'history.crop.presets.twoInch.title',
        descriptionKey: 'history.crop.presets.twoInch.description',
        countries: ['CN'],
        variations: [
          {
            id: 'two-inch-standard',
            labelKey: 'history.crop.presets.twoInch.standard',
            descriptionKey: 'history.crop.presets.twoInch.standardHint',
            widthMm: 35,
            heightMm: 53,
            countries: ['CN']
          },
          {
            id: 'two-inch-small',
            labelKey: 'history.crop.presets.twoInch.small',
            descriptionKey: 'history.crop.presets.twoInch.smallHint',
            widthMm: 35,
            heightMm: 45,
            countries: ['CN']
          }
        ]
      }
    ]
  },
  {
    id: 'passport',
    titleKey: 'history.crop.groups.passport.title',
    descriptionKey: 'history.crop.groups.passport.description',
    icon: '🛂',
    options: [
      {
        id: 'passport-cn',
        labelKey: 'history.crop.presets.passportCn.title',
        descriptionKey: 'history.crop.presets.passportCn.description',
        countries: ['CN'],
        variations: [
          {
            id: 'passport-cn-default',
            labelKey: 'history.crop.presets.passportCn.standard',
            widthMm: 33,
            heightMm: 48,
            countries: ['CN']
          }
        ]
      },
      {
        id: 'passport-us',
        labelKey: 'history.crop.presets.passportUs.title',
        descriptionKey: 'history.crop.presets.passportUs.description',
        countries: ['US'],
        variations: [
          {
            id: 'passport-us-standard',
            labelKey: 'history.crop.presets.passportUs.standard',
            widthMm: 51,
            heightMm: 51,
            countries: ['US']
          }
        ]
      }
    ]
  },
  {
    id: 'visa',
    titleKey: 'history.crop.groups.visa.title',
    descriptionKey: 'history.crop.groups.visa.description',
    icon: '🛬',
    options: [
      {
        id: 'visa-cn',
        labelKey: 'history.crop.presets.visaCn.title',
        descriptionKey: 'history.crop.presets.visaCn.description',
        countries: ['CN'],
        variations: [
          {
            id: 'visa-cn-standard',
            labelKey: 'history.crop.presets.visaCn.standard',
            widthMm: 33,
            heightMm: 48,
            countries: ['CN']
          }
        ]
      },
      {
        id: 'visa-schengen',
        labelKey: 'history.crop.presets.visaSchengen.title',
        descriptionKey: 'history.crop.presets.visaSchengen.description',
        variations: [
          {
            id: 'visa-schengen-standard',
            labelKey: 'history.crop.presets.visaSchengen.standard',
            widthMm: 35,
            heightMm: 45
          }
        ]
      }
    ]
  },
  {
    id: 'general',
    titleKey: 'history.crop.groups.general.title',
    descriptionKey: 'history.crop.groups.general.description',
    icon: '✨',
    options: [
      {
        id: 'general-square',
        labelKey: 'history.crop.presets.generalSquare.title',
        descriptionKey: 'history.crop.presets.generalSquare.description',
        variations: [
          {
            id: 'general-square-standard',
            labelKey: 'history.crop.presets.generalSquare.standard',
            widthMm: 40,
            heightMm: 40
          }
        ]
      },
      {
        id: 'general-portrait',
        labelKey: 'history.crop.presets.generalPortrait.title',
        descriptionKey: 'history.crop.presets.generalPortrait.description',
        variations: [
          {
            id: 'general-portrait-standard',
            labelKey: 'history.crop.presets.generalPortrait.standard',
            widthMm: 40,
            heightMm: 60
          }
        ]
      },
      {
        id: 'general-landscape',
        labelKey: 'history.crop.presets.generalLandscape.title',
        descriptionKey: 'history.crop.presets.generalLandscape.description',
        variations: [
          {
            id: 'general-landscape-standard',
            labelKey: 'history.crop.presets.generalLandscape.standard',
            widthMm: 60,
            heightMm: 40
          }
        ]
      }
    ]
  }
];

function flattenPresets(groups: CropPresetGroup[]): Map<string, FlattenedPreset> {
  const map = new Map<string, FlattenedPreset>();
  groups.forEach((group) => {
    group.options.forEach((option) => {
      option.variations?.forEach((variation) => {
        map.set(variation.id, {
          id: variation.id,
          labelKey: variation.labelKey,
          descriptionKey: variation.descriptionKey ?? option.descriptionKey,
          widthMm: variation.widthMm,
          heightMm: variation.heightMm,
          countries: variation.countries ?? option.countries,
          parentLabelKey: option.labelKey
        });
      });
    });
  });
  return map;
}

const presetIndex = flattenPresets(CROP_PRESET_GROUPS);

export function getPresetById(id: string | null | undefined): FlattenedPreset | undefined {
  if (!id) {
    return undefined;
  }
  return presetIndex.get(id);
}

export function getGroupsForCountry(countryCode: string): CropPresetGroup[] {
  return CROP_PRESET_GROUPS.map((group) => ({
    ...group,
    options: group.options
      .map((option) => {
        const matchesCountry =
          !option.countries?.length || option.countries.includes(countryCode);
        const variations = option.variations?.filter(
          (variation) =>
            !variation.countries?.length || variation.countries.includes(countryCode)
        );
        if (!matchesCountry && !variations?.length) {
          return null;
        }
        return {
          ...option,
          variations
        };
      })
      .filter((option): option is CropPresetOption => Boolean(option && option.variations?.length))
  })).filter((group) => group.options.length);
}

export function buildCropMetadata(params: {
  countryCode: string;
  unit: CropUnit;
  preset?: FlattenedPreset | null;
  sizeMm: { width: number; height: number };
}): HistoryResultMetadata['crop'] {
  return {
    countryCode: params.countryCode,
    presetId: params.preset?.id ?? null,
    presetLabel: params.preset?.labelKey ?? null,
    unit: params.unit,
    sizeMm: params.sizeMm,
    dpi: 300,
    updatedAt: new Date().toISOString()
  };
}
